import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import FeedbackButtons from './FeedbackButtons';
import ArticleEditor from './ArticleEditor';
import DebriefPanel from './DebriefPanel';
import Portal from './Portal';
import {
  Plus, Search, Edit2, Trash2, Save, ArrowLeft,
  FileText, Sparkles, Loader2, X, ChevronDown, ChevronRight,
  Send, Globe, Facebook, Instagram, Linkedin, Check
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Rotating status messages shown during article generation (can take 1–3 min).
const GEN_MESSAGES = [
  'Researching your topic…',
  'Structuring the sections…',
  'Optimizing for search…',
  'Polishing the draft…',
];

const FREE_ARTICLE_LIMIT = 3;  // must match backend TIER_CONFIG[FREE].max_articles

const ArticleList = ({ onNavigate }) => {
  const { getAuthHeaders, subscription, refreshSubscription } = useAuth();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showGenerator, setShowGenerator] = useState(false);
  const [generating, setGenerating] = useState(false);
  
  const [genTitle, setGenTitle] = useState('');
  const [genKeywords, setGenKeywords] = useState('');
  const [genLocation, setGenLocation] = useState('');
  const [genPlatform, setGenPlatform] = useState('website');
  const [genTone, setGenTone] = useState('professional');
  const [genLength, setGenLength] = useState('medium');
  const [genBrandVoice, setGenBrandVoice] = useState('');
  // §5 Stage 1 — intent capture (optional, collapsed by default)
  const [showIntent, setShowIntent] = useState(false);
  const [genIntentAchieve, setGenIntentAchieve] = useState('');
  const [genIntentAudience, setGenIntentAudience] = useState('');
  const [genIntentAction, setGenIntentAction] = useState('');
  const [brandVoices, setBrandVoices] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [publishDropdown, setPublishDropdown] = useState(null);
  const [publishing, setPublishing] = useState(null);
  const [publishResult, setPublishResult] = useState(null);
  const [selectedArticles, setSelectedArticles] = useState(new Set());
  const [showBulkPublish, setShowBulkPublish] = useState(false);
  const [bulkPublishing, setBulkPublishing] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);
  const [editingArticle, setEditingArticle] = useState(null);
  const [showDebrief, setShowDebrief] = useState(false);  // §5 Stage 2 debrief panel
  const [freshArticleId, setFreshArticleId] = useState(null);  // auto-trigger debrief for this just-generated article
  const [editContent, setEditContent] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [genMsgIdx, setGenMsgIdx] = useState(0);       // index into GEN_MESSAGES while generating
  const [atLimitPrompt, setAtLimitPrompt] = useState(false);  // show the upgrade prompt in the modal
  const dropdownRef = useRef(null);

  useEffect(() => { fetchArticles(); fetchBrandVoices(); fetchIntegrations(); }, [statusFilter]);

  // Rotate the loading status messages while an article is generating.
  useEffect(() => {
    if (!generating) { setGenMsgIdx(0); return; }
    const id = setInterval(() => setGenMsgIdx((i) => (i + 1) % GEN_MESSAGES.length), 3200);
    return () => clearInterval(id);
  }, [generating]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setPublishDropdown(null);
        setPublishResult(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const fetchBrandVoices = async () => {
    try {
      const response = await axios.get(`${API}/brand/voices`, { headers: getAuthHeaders() });
      setBrandVoices(response.data);
    } catch (err) { /* brand voices are optional */ }
  };

  const fetchIntegrations = async () => {
    try {
      const response = await axios.get(`${API}/integrations`, { headers: getAuthHeaders() });
      setIntegrations(response.data);
    } catch (err) { /* integrations optional */ }
  };

  const handlePublish = async (articleId, integrationId) => {
    setPublishing(integrationId);
    setPublishResult(null);
    try {
      const response = await axios.post(`${API}/publish`, {
        article_id: articleId,
        integration_id: integrationId,
      }, { headers: getAuthHeaders() });
      setPublishResult({ success: true, message: response.data.message, url: response.data.url });
      fetchArticles();
    } catch (err) {
      setPublishResult({ success: false, message: err.response?.data?.detail || 'Publishing failed' });
    } finally { setPublishing(null); }
  };

  const toggleSelect = (id) => {
    setSelectedArticles(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedArticles.size === filteredArticles.length) {
      setSelectedArticles(new Set());
    } else {
      setSelectedArticles(new Set(filteredArticles.map(a => a.id)));
    }
  };

  const handleBulkPublish = async (integrationId) => {
    setBulkPublishing(true);
    setBulkResults(null);
    try {
      const response = await axios.post(`${API}/publish/bulk`, {
        article_ids: Array.from(selectedArticles),
        integration_id: integrationId,
      }, { headers: getAuthHeaders() });
      setBulkResults(response.data);
      fetchArticles();
    } catch (err) {
      setBulkResults({ error: err.response?.data?.detail || 'Bulk publish failed' });
    } finally { setBulkPublishing(false); }
  };

  const openEditor = (article) => {
    setEditingArticle(article);
    setEditTitle(article.title || '');
    setEditContent(article.content || '');
  };

  const saveArticle = async () => {
    if (!editingArticle) return;
    setSaving(true);
    try {
      await axios.put(`${API}/articles/${editingArticle.id}`, {
        title: editTitle,
        content: editContent,
      }, { headers: getAuthHeaders() });
      setEditingArticle(null);
      fetchArticles();
    } catch (err) {
      console.error('Save failed:', err);
    } finally { setSaving(false); }
  };

  const fetchArticles = async () => {
    try {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const response = await axios.get(`${API}/articles${params}`, { headers: getAuthHeaders() });
      setArticles(response.data);
    } catch (error) {
      console.error('Failed to fetch articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    // Free users at the lifetime cap: show the crafting state briefly, THEN the
    // upgrade prompt — without calling the API, so nothing is generated or charged.
    const atFreeLimit = subscription?.tier === 'free'
      && (subscription?.article_count_lifetime || 0) >= FREE_ARTICLE_LIMIT;
    if (atFreeLimit) {
      setAtLimitPrompt(false);
      setGenerating(true);
      await new Promise((r) => setTimeout(r, 1800));
      setGenerating(false);
      setAtLimitPrompt(true);
      return;
    }
    setGenerating(true);
    try {
      const anyIntent = genIntentAchieve || genIntentAudience.trim() || genIntentAction.trim();
      const response = await axios.post(`${API}/articles/generate`, {
        title: genTitle,
        keywords: genKeywords.split(',').map(k => k.trim()).filter(Boolean),
        target_location: genLocation,
        target_platform: genPlatform,
        tone: genTone,
        length: genLength,
        brand_voice_id: genBrandVoice || undefined,
        ...(anyIntent && { intent: {
          achieve: genIntentAchieve,
          audience: genIntentAudience.trim(),
          action: genIntentAction.trim(),
        } })
      }, { headers: getAuthHeaders() });
      const newArticle = response.data.article;
      setArticles([newArticle, ...articles]);
      setShowGenerator(false);
      setGenTitle('');
      setGenKeywords('');
      setGenLocation('');
      setShowIntent(false);
      setGenIntentAchieve('');
      setGenIntentAudience('');
      setGenIntentAction('');
      // Auto-flow: land in the editor with the coworker debrief already opening.
      openEditor(newArticle);
      setFreshArticleId(newArticle.id);
      setShowDebrief(true);
      await refreshSubscription();
    } catch (error) {
      const status = error.response?.status;
      // No response (network/proxy timeout) or a gateway timeout means the backend
      // may still be finishing — the article can land in the DB even though we never
      // got the response. Do NOT tell the user it failed (that invites a double
      // generation + double charge); point them to their list instead, and refresh it.
      const looksLikeTimeout = !error.response || status === 504 || status === 502 || error.code === 'ECONNABORTED';
      if (looksLikeTimeout) {
        setShowGenerator(false);
        await fetchArticles();
        await refreshSubscription();
        alert('Generation is taking longer than usual — check your Articles list in a minute before retrying, so you don\'t generate (and get charged for) it twice.');
      } else {
        alert(error.response?.data?.detail || 'Failed to generate article');
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (articleId) => {
    if (!window.confirm('Delete this article?')) return;
    try {
      await axios.delete(`${API}/articles/${articleId}`, { headers: getAuthHeaders() });
      setArticles(articles.filter(a => a.id !== articleId));
    } catch (error) {
      console.error('Failed to delete:', error);
    }
  };

  const filteredArticles = articles.filter(article => 
    article.title?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getSeoScoreClass = (score) => {
    if (score >= 70) return 'excellent';
    if (score >= 50) return 'good';
    return 'poor';
  };

  // Full-page editor view (replaces the old modal overlay). The sidebar stays
  // visible because App renders it outside this component's <main>. The content
  // area fills the viewport and scrolls on its own (fillHeight).
  if (editingArticle) {
    return (
      <div className="fade-in flex flex-col h-[calc(100vh-9rem)] md:h-[calc(100vh-3.5rem)]" data-testid="article-editor-page">
        <div className="flex items-center gap-3 mb-4 flex-shrink-0">
          <button onClick={() => { setEditingArticle(null); setShowDebrief(false); setFreshArticleId(null); }} className="btn btn-ghost flex items-center gap-1.5 text-sm" data-testid="editor-back-btn">
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <input
            type="text" value={editTitle} onChange={(e) => setEditTitle(e.target.value)}
            className="font-serif text-2xl flex-1 min-w-0 bg-transparent border-none outline-none"
            placeholder="Article title..." data-testid="editor-title-input"
          />
          <button onClick={() => setShowDebrief(true)} className="btn btn-secondary text-sm flex items-center gap-1.5 flex-shrink-0" data-testid="open-debrief-btn">
            <Sparkles className="w-4 h-4" />
            Debrief
          </button>
          <button onClick={saveArticle} disabled={saving} className="btn btn-primary text-sm flex items-center gap-1.5 flex-shrink-0" data-testid="editor-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </button>
        </div>
        <div className="flex-1 min-h-0">
          <ArticleEditor content={editContent} onChange={setEditContent} fillHeight />
        </div>
        {showDebrief && (
          <DebriefPanel
            articleId={editingArticle.id}
            voiceId={editingArticle.brand_voice_id}
            autoGenerate={freshArticleId === editingArticle.id}
            getAuthHeaders={getAuthHeaders}
            onClose={() => setShowDebrief(false)}
            onFactSaved={() => {}}
          />
        )}
      </div>
    );
  }

  return (
    <div className="fade-in" data-testid="articles-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="font-serif text-3xl mb-1">Articles</h1>
          <p className="text-sm text-[var(--color-muted)]">
            {articles.length} article{articles.length !== 1 ? 's' : ''} &middot;&nbsp;
            {subscription?.article_count_lifetime || 0}/{subscription?.tier === 'free' ? '3 lifetime' : `${subscription?.max_articles || 10}/mo`}
          </p>
        </div>
        <button 
          onClick={() => { setShowGenerator(true); setAtLimitPrompt(false); }}
          className="btn btn-primary"
          data-testid="new-article-btn"
        >
          <Plus className="w-4 h-4" />
          New Article
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-muted)]" />
          <input
            type="text"
            placeholder="Search articles..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pl-10"
            data-testid="search-input"
          />
        </div>
        <div className="relative">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input appearance-none pr-10 cursor-pointer"
            data-testid="status-filter"
          >
            <option value="all">All Status</option>
            <option value="draft">Drafts</option>
            <option value="published">Published</option>
            <option value="scheduled">Scheduled</option>
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-muted)] pointer-events-none" />
        </div>
      </div>

      {/* Bulk Action Bar */}
      {selectedArticles.size > 0 && (
        <div className="flex items-center justify-between p-3 mb-4 rounded-xl bg-[var(--color-charcoal)] text-white animate-in" data-testid="bulk-action-bar">
          <div className="flex items-center gap-3">
            <button onClick={toggleSelectAll} className="w-5 h-5 rounded border-2 border-white/40 flex items-center justify-center hover:border-white transition-colors" data-testid="bulk-select-all-btn">
              {selectedArticles.size === filteredArticles.length && <Check className="w-3 h-3" />}
            </button>
            <span className="text-xs font-medium">{selectedArticles.size} article{selectedArticles.size !== 1 ? 's' : ''} selected</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setShowBulkPublish(true); setBulkResults(null); }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-xs font-medium transition-colors"
              data-testid="bulk-publish-btn"
            >
              <Send className="w-3.5 h-3.5" />
              Publish to...
            </button>
            <button
              onClick={() => setSelectedArticles(new Set())}
              className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
              data-testid="bulk-deselect-btn"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* Articles List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner"></div>
        </div>
      ) : filteredArticles.length === 0 ? (
        <div className="card p-14 text-center">
          <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-4 bg-[var(--color-surface)]">
            <FileText className="w-7 h-7 text-[var(--color-coral)]" />
          </div>
          <h3 className="font-display text-base font-medium mb-2">No articles found</h3>
          <p className="text-sm text-[var(--color-muted)] mb-6">
            {searchQuery ? 'Try a different search term' : 'Start building your content pillars'}
          </p>
          {!searchQuery && (
            <button onClick={() => { setShowGenerator(true); setAtLimitPrompt(false); }} className="btn btn-primary text-sm">
              <Sparkles className="w-4 h-4" />
              Generate your first article
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredArticles.map((article) => (
            <div key={article.id} className={`article-card ${selectedArticles.has(article.id) ? 'ring-2 ring-[var(--color-coral)] ring-offset-1' : ''}`} data-testid={`article-${article.id}`}>
              <div className="article-card-content flex gap-3">
                <button
                  onClick={() => toggleSelect(article.id)}
                  className={`mt-1 flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                    selectedArticles.has(article.id)
                      ? 'bg-[var(--color-coral)] border-[var(--color-coral)] text-white'
                      : 'border-[var(--color-border)] hover:border-[var(--color-charcoal)]'
                  }`}
                  data-testid={`select-article-${article.id}`}
                >
                  {selectedArticles.has(article.id) && <Check className="w-3 h-3" />}
                </button>
                <div className="min-w-0 flex-1">
                  <h3 className="article-title">{article.title || 'Untitled'}</h3>
                <p className="text-xs text-[var(--color-muted)] line-clamp-2 mb-2">
                  {article.meta_description || 'No description'}
                </p>
                <div className="article-meta">
                  <span className={`badge badge-${article.status}`}>{article.status}</span>
                  <span>{article.target_platform}</span>
                  <span>{new Date(article.created_at).toLocaleDateString()}</span>
                </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <FeedbackButtons targetType="article" targetId={article.id} />
                <div className={`seo-score ${getSeoScoreClass(article.seo_score || 0)}`}
                     style={{'--score': `${(article.seo_score || 0) * 3.6}deg`}}>
                  <span>{article.seo_score || 0}</span>
                </div>
                <div className="flex gap-1 relative">
                  {/* Publish Button */}
                  <div className="relative" ref={publishDropdown === article.id ? dropdownRef : undefined}>
                    <button
                      className="btn btn-ghost p-2"
                      title={integrations.length > 0 ? 'Publish to platform' : 'Connect a platform first'}
                      onClick={() => {
                        setPublishDropdown(publishDropdown === article.id ? null : article.id);
                        setPublishResult(null);
                      }}
                      data-testid={`publish-btn-${article.id}`}
                    >
                      <Send className="w-3.5 h-3.5" />
                    </button>

                    {publishDropdown === article.id && (
                      <div className="absolute right-0 top-full mt-1 w-64 bg-white rounded-xl shadow-xl border border-[var(--color-border)] z-50 overflow-hidden" data-testid="publish-dropdown">
                        <div className="p-3 border-b border-[var(--color-border)]">
                          <p className="text-xs font-medium">Publish to Platform</p>
                          <p className="text-[9px] text-[var(--color-muted)]">
                            {article.published_to?.length > 0
                              ? `Already published to ${article.published_to.length} platform(s)`
                              : 'Select a connected platform'}
                          </p>
                        </div>
                        {integrations.length > 0 ? (
                          <div className="max-h-48 overflow-y-auto">
                            {integrations.map((integration) => {
                              const alreadyPublished = article.published_to?.some(p => p.integration_id === integration.id);
                              return (
                                <button
                                  key={integration.id}
                                  className={`w-full flex items-center gap-3 p-3 text-left text-xs hover:bg-[var(--color-surface)] transition-colors ${alreadyPublished ? 'opacity-50' : ''}`}
                                  onClick={() => !alreadyPublished && handlePublish(article.id, integration.id)}
                                  disabled={publishing || alreadyPublished}
                                  data-testid={`publish-to-${integration.id}`}
                                >
                                  <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-[var(--color-surface)]">
                                    {integration.platform === 'facebook' ? <Facebook className="w-3.5 h-3.5" /> :
                                     integration.platform === 'instagram' ? <Instagram className="w-3.5 h-3.5" /> :
                                     integration.platform === 'linkedin' ? <Linkedin className="w-3.5 h-3.5" /> :
                                     <Globe className="w-3.5 h-3.5" />}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium truncate">{integration.site_name}</p>
                                    <p className="text-[9px] text-[var(--color-muted)] capitalize">{integration.platform}</p>
                                  </div>
                                  {publishing === integration.id ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin text-[var(--color-coral)]" />
                                  ) : alreadyPublished ? (
                                    <Check className="w-3.5 h-3.5 text-emerald-500" />
                                  ) : null}
                                </button>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="p-4 text-center">
                            <p className="text-[10px] text-[var(--color-muted)] mb-2">No platforms connected yet</p>
                          </div>
                        )}
                        {publishResult && (
                          <div className={`mx-3 mb-3 p-2.5 rounded-lg text-[10px] ${publishResult.success ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`} data-testid="publish-result">
                            {publishResult.message}
                            {publishResult.url && (
                              <a href={publishResult.url} target="_blank" rel="noopener noreferrer" className="block underline mt-1">View post</a>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <button className="btn btn-ghost p-2" title="Edit" onClick={() => openEditor(article)} data-testid={`edit-btn-${article.id}`}>
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => handleDelete(article.id)} className="btn btn-ghost p-2" title="Delete">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Generator Modal */}
      {showGenerator && (
        <Portal>
        <div className="fixed inset-0 bg-black/30 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="relative bg-white rounded-[20px] w-full max-w-2xl my-8 shadow-2xl max-h-[85vh] flex flex-col overflow-hidden" data-testid="article-generate-modal">
            {generating ? (
              /* Loading view (#3): rotating status + discrete animation */
              <div className="p-12 text-center" data-testid="generating-state">
                <div className="relative w-16 h-16 mx-auto mb-6">
                  <div className="absolute inset-0 rounded-full bg-[var(--color-coral)]/15 animate-ping" />
                  <div className="relative w-16 h-16 rounded-full bg-[var(--color-coral)]/10 flex items-center justify-center">
                    <Sparkles className="w-7 h-7 text-[var(--color-coral)] animate-pulse" />
                  </div>
                </div>
                <p className="font-serif text-xl mb-1" data-testid="gen-status-msg">{GEN_MESSAGES[genMsgIdx]}</p>
                <p className="text-xs text-[var(--color-muted)]">Crafting your article — this can take 1–3 minutes, especially with a brand voice attached.</p>
                <div className="flex items-center justify-center gap-1.5 mt-5">
                  {GEN_MESSAGES.map((_, i) => (
                    <div key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i === genMsgIdx ? 'w-6 bg-[var(--color-coral)]' : 'w-1.5 bg-[var(--color-border)]'}`} />
                  ))}
                </div>
              </div>
            ) : atLimitPrompt ? (
              /* Upgrade prompt (#2): shown after the brief crafting state at the free cap */
              <div className="p-10 text-center" data-testid="upgrade-prompt">
                <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-4 bg-[var(--color-coral)]/10">
                  <Sparkles className="w-7 h-7 text-[var(--color-coral)]" />
                </div>
                <h2 className="font-serif text-2xl mb-2">You've reached your {FREE_ARTICLE_LIMIT} free articles</h2>
                <p className="text-sm text-[var(--color-muted)] mb-6">Upgrade to keep creating — more articles, more credits, and premium features.</p>
                <div className="flex gap-3">
                  <button onClick={() => { setShowGenerator(false); setAtLimitPrompt(false); }} className="btn btn-secondary flex-1">Maybe later</button>
                  <button onClick={() => { setShowGenerator(false); setAtLimitPrompt(false); onNavigate?.('credits'); }} className="btn btn-primary flex-1" data-testid="upgrade-cta-btn">Unlock more</button>
                </div>
              </div>
            ) : (
            <>
            <div className="p-6 border-b border-[var(--color-border)] flex items-center justify-between">
              <div>
                <h2 className="font-serif text-xl">Generate Article</h2>
                <p className="text-xs text-[var(--color-muted)]">2.5 credits per article</p>
              </div>
              <button onClick={() => setShowGenerator(false)} className="btn btn-ghost p-2">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleGenerate} onKeyDown={(e) => { if (e.key === 'Enter' && e.target.tagName === 'INPUT') e.preventDefault(); }} className="flex flex-col flex-1 min-h-0">
              <div className="p-6 space-y-4 overflow-y-auto flex-1 min-h-0">
              <div>
                <label className="block text-xs font-medium mb-1.5">Article Title *</label>
                <input type="text" value={genTitle} onChange={(e) => setGenTitle(e.target.value)}
                  className="input" placeholder="e.g., 10 Ways to Improve Your SEO" required data-testid="gen-title-input" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1.5">Keywords (comma separated)</label>
                <input type="text" value={genKeywords} onChange={(e) => setGenKeywords(e.target.value)}
                  className="input" placeholder="SEO, content marketing, small business" data-testid="gen-keywords-input" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1.5">Target Location</label>
                <input type="text" value={genLocation} onChange={(e) => setGenLocation(e.target.value)}
                  className="input" placeholder="e.g., New York, Europe, Global" data-testid="gen-location-input" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1.5">Platform</label>
                  <select value={genPlatform} onChange={(e) => setGenPlatform(e.target.value)} className="input">
                    <option value="website">Website</option>
                    <option value="wordpress">WordPress</option>
                    <option value="medium">Medium</option>
                    <option value="linkedin">LinkedIn</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5">Tone</label>
                  <select value={genTone} onChange={(e) => setGenTone(e.target.value)} className="input">
                    <option value="professional">Professional</option>
                    <option value="casual">Casual</option>
                    <option value="friendly">Friendly</option>
                    <option value="authoritative">Authoritative</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5">Length</label>
                  <select value={genLength} onChange={(e) => setGenLength(e.target.value)} className="input">
                    <option value="short">Short</option>
                    <option value="medium">Medium</option>
                    <option value="long">Long</option>
                  </select>
                </div>
              </div>
              {brandVoices.length > 0 && (
                <div>
                  <label className="block text-xs font-medium mb-1.5">Brand Voice (optional)</label>
                  <select value={genBrandVoice} onChange={(e) => setGenBrandVoice(e.target.value)} className="input" data-testid="gen-brand-voice">
                    <option value="">No brand voice</option>
                    {brandVoices.map(v => (
                      <option key={v.id} value={v.id}>{v.name}</option>
                    ))}
                  </select>
                  <p className="text-[10px] text-[var(--color-muted)] mt-1">Articles will match your brand's tone and style</p>
                </div>
              )}
              {/* §5 Stage 1 — intent capture (optional, collapsed by default) */}
              <div className="border-t border-[var(--color-border)] pt-3">
                <button type="button" onClick={() => setShowIntent(v => !v)}
                  className="flex items-center gap-1.5 text-xs font-medium text-[var(--color-muted)] hover:text-[var(--color-charcoal)] transition-colors"
                  data-testid="toggle-intent">
                  <ChevronRight className={`w-3.5 h-3.5 transition-transform ${showIntent ? 'rotate-90' : ''}`} />
                  What's this piece for? <span className="font-normal">(optional)</span>
                </button>
                {showIntent && (
                  <div className="mt-3 space-y-3" data-testid="intent-fields">
                    <div>
                      <label className="block text-xs font-medium mb-1.5">Goal</label>
                      <select value={genIntentAchieve} onChange={(e) => setGenIntentAchieve(e.target.value)} className="input" data-testid="intent-achieve">
                        <option value="">—</option>
                        <option value="Rank in search">Rank in search</option>
                        <option value="Educate">Educate</option>
                        <option value="Convert">Convert</option>
                        <option value="Defend a position">Defend a position</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1.5">Reader</label>
                      <input type="text" value={genIntentAudience} onChange={(e) => setGenIntentAudience(e.target.value)}
                        className="input" placeholder="Who's reading, and what do they already believe?" data-testid="intent-audience" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1.5">Desired action</label>
                      <input type="text" value={genIntentAction} onChange={(e) => setGenIntentAction(e.target.value)}
                        className="input" placeholder="What should they do by the end?" data-testid="intent-action" />
                    </div>
                  </div>
                )}
              </div>
              </div>
              <div className="p-6 pt-4 flex gap-3 flex-shrink-0 border-t border-[var(--color-border)]">
                <button type="button" onClick={() => setShowGenerator(false)} className="btn btn-secondary flex-1">Cancel</button>
                <button type="submit" disabled={generating || !genTitle} className="btn btn-primary flex-1" data-testid="generate-btn">
                  {generating ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</>
                  ) : (
                    <><Sparkles className="w-4 h-4" /> Generate Article</>
                  )}
                </button>
              </div>
            </form>
            </>
            )}
          </div>
        </div>
        </Portal>
      )}

      {/* Bulk Publish Modal */}
      {showBulkPublish && (
        <Portal>
        <div className="fixed inset-0 bg-black/30 flex items-start justify-center z-50 p-4 overflow-y-auto" data-testid="bulk-publish-modal">
          <div className="bg-white rounded-[20px] w-full max-w-md my-8 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b border-[var(--color-border)] flex items-center justify-between">
              <div>
                <h2 className="font-serif text-lg">Bulk Publish</h2>
                <p className="text-[10px] text-[var(--color-muted)]">{selectedArticles.size} article{selectedArticles.size !== 1 ? 's' : ''} selected</p>
              </div>
              <button onClick={() => setShowBulkPublish(false)} className="btn btn-ghost p-2" data-testid="close-bulk-modal">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5">
              {!bulkResults ? (
                <>
                  <p className="text-xs text-[var(--color-muted)] mb-4">Choose a platform to publish all selected articles:</p>
                  {integrations.length > 0 ? (
                    <div className="space-y-2">
                      {integrations.map((integration) => (
                        <button
                          key={integration.id}
                          onClick={() => handleBulkPublish(integration.id)}
                          disabled={bulkPublishing}
                          className="w-full flex items-center gap-3 p-3.5 rounded-xl border border-[var(--color-border)] text-left text-xs hover:bg-[var(--color-surface)] transition-colors"
                          data-testid={`bulk-publish-to-${integration.id}`}
                        >
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[var(--color-surface)]">
                            {integration.platform === 'facebook' ? <Facebook className="w-4 h-4" /> :
                             integration.platform === 'instagram' ? <Instagram className="w-4 h-4" /> :
                             integration.platform === 'linkedin' ? <Linkedin className="w-4 h-4" /> :
                             <Globe className="w-4 h-4" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium">{integration.site_name}</p>
                            <p className="text-[9px] text-[var(--color-muted)] capitalize">{integration.platform}</p>
                          </div>
                          {bulkPublishing && <Loader2 className="w-4 h-4 animate-spin text-[var(--color-coral)]" />}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <p className="text-xs text-[var(--color-muted)]">No platforms connected. Go to Integrations to add one.</p>
                    </div>
                  )}
                </>
              ) : bulkResults.error ? (
                <div className="p-4 rounded-xl bg-red-50 text-red-700 text-xs" data-testid="bulk-result-error">
                  {bulkResults.error}
                </div>
              ) : (
                <div data-testid="bulk-results">
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${bulkResults.failed === 0 ? 'bg-emerald-50' : 'bg-amber-50'}`}>
                      {bulkResults.failed === 0
                        ? <Check className="w-5 h-5 text-emerald-600" />
                        : <Send className="w-5 h-5 text-amber-600" />}
                    </div>
                    <div>
                      <p className="text-sm font-medium">
                        {bulkResults.succeeded}/{bulkResults.total} published
                      </p>
                      <p className="text-[10px] text-[var(--color-muted)]">
                        {bulkResults.failed > 0 ? `${bulkResults.failed} failed` : 'All articles published successfully'}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-1.5 max-h-48 overflow-y-auto">
                    {bulkResults.results?.map((r, i) => (
                      <div key={i} className={`flex items-center gap-2 p-2.5 rounded-lg text-[10px] ${r.success ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`} data-testid={`bulk-result-${i}`}>
                        {r.success ? <Check className="w-3 h-3 flex-shrink-0" /> : <X className="w-3 h-3 flex-shrink-0" />}
                        <span className="truncate flex-1">{r.title || r.article_id}</span>
                        {r.url && <a href={r.url} target="_blank" rel="noopener noreferrer" className="underline flex-shrink-0">View</a>}
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => { setShowBulkPublish(false); setSelectedArticles(new Set()); setBulkResults(null); }}
                    className="btn btn-primary w-full mt-4 text-xs"
                    data-testid="bulk-done-btn"
                  >
                    Done
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        </Portal>
      )}
    </div>
  );
};

export default ArticleList;
